/**
 * Admin portal RTK API — posts, reports, FAQs, settings, push.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { strideBaseQuery } from '@/services/rtkBaseQuery'
import { unwrapPayload } from '@/data/shared/unwrap_payload'

function extractItems<T>(response: unknown): T[] {
  const data = unwrapPayload(response)
  const items = data.items
  return Array.isArray(items) ? (items as T[]) : []
}

export const adminPortalApi = createApi({
  reducerPath: 'adminPortalApi',
  baseQuery: strideBaseQuery,
  tagTypes: ['AdminPost', 'AdminReport', 'AdminFaq', 'AdminSettings'],
  endpoints: builder => ({
    getAdminPosts: builder.query<
      Array<{
        id: string
        content: string
        imageUrl?: string | null
        postedBy: string
        createdAt: string
      }>,
      { page?: number; limit?: number } | void
    >({
      query: arg => ({
        url: '/admin/posts',
        params: {
          page: arg && typeof arg === 'object' ? arg.page ?? 0 : 0,
          limit: arg && typeof arg === 'object' ? arg.limit ?? 50 : 50,
        },
      }),
      transformResponse: response => extractItems(response),
      providesTags: ['AdminPost'],
    }),
    deleteAdminPost: builder.mutation<void, string>({
      query: id => ({
        url: `/admin/posts/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminPost'],
    }),
    getReportedPosts: builder.query<
      Array<{
        id: string
        postId: string
        content: string
        imageUrl?: string | null
        postedBy: string
        reportedBy: string
        reason: string
        createdAt: string
      }>,
      { page?: number; limit?: number } | void
    >({
      query: arg => ({
        url: '/admin/posts/reported',
        params: {
          page: arg && typeof arg === 'object' ? arg.page ?? 0 : 0,
          limit: arg && typeof arg === 'object' ? arg.limit ?? 50 : 50,
        },
      }),
      transformResponse: response => extractItems(response),
      providesTags: ['AdminReport'],
    }),
    hideReportedPost: builder.mutation<void, string>({
      query: reportId => ({
        url: `/admin/posts/reported/${encodeURIComponent(reportId)}/post`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminReport', 'AdminPost'],
    }),
    getFaqs: builder.query<
      Array<{
        id: string
        question: string
        answer: string
        sortOrder: number
        isActive: boolean
        createdAt: string
      }>,
      void
    >({
      query: () => '/admin/faqs',
      transformResponse: response => extractItems(response),
      providesTags: ['AdminFaq'],
    }),
    getFaqById: builder.query<
      { id: string; question: string; answer: string },
      string
    >({
      query: id => `/admin/faqs/${encodeURIComponent(id)}`,
      transformResponse: response => unwrapPayload(response) as { id: string; question: string; answer: string },
      providesTags: (_r, _e, id) => [{ type: 'AdminFaq', id }],
    }),
    createFaq: builder.mutation<
      unknown,
      { question: string; answer: string }
    >({
      query: body => ({
        url: '/admin/faqs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminFaq'],
    }),
    updateFaq: builder.mutation<
      unknown,
      { id: string; question: string; answer: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/faqs/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminFaq'],
    }),
    deleteFaq: builder.mutation<void, string>({
      query: id => ({
        url: `/admin/faqs/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminFaq'],
    }),
    getSupportSettings: builder.query<
      { email: string; mobileNumber: string },
      void
    >({
      query: () => '/admin/settings/support',
      transformResponse: response =>
        unwrapPayload(response) as { email: string; mobileNumber: string },
      providesTags: ['AdminSettings'],
    }),
    saveSupportSettings: builder.mutation<
      unknown,
      { email?: string; mobileNumber?: string }
    >({
      query: body => ({
        url: '/admin/settings/support',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminSettings'],
    }),
    getApiKeysSettings: builder.query<
      {
        fcmKey: string
        twilioAccountSid: string
        twilioAuthToken: string
      },
      void
    >({
      query: () => '/admin/settings/api-keys',
      transformResponse: response =>
        unwrapPayload(response) as {
          fcmKey: string
          twilioAccountSid: string
          twilioAuthToken: string
        },
      providesTags: ['AdminSettings'],
    }),
    saveApiKeysSettings: builder.mutation<
      unknown,
      {
        fcmKey?: string
        twilioAccountSid?: string
        twilioAuthToken?: string
      }
    >({
      query: body => ({
        url: '/admin/settings/api-keys',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminSettings'],
    }),
    getTimezoneSettings: builder.query<{ timezone: string }, void>({
      query: () => '/admin/settings/timezone',
      transformResponse: response =>
        unwrapPayload(response) as { timezone: string },
      providesTags: ['AdminSettings'],
    }),
    saveTimezoneSettings: builder.mutation<unknown, { timezone: string }>({
      query: body => ({
        url: '/admin/settings/timezone',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminSettings'],
    }),
    getAboutContent: builder.query<
      { title: string; description: string },
      void
    >({
      query: () => '/admin/cms/about',
      transformResponse: response =>
        unwrapPayload(response) as { title: string; description: string },
      providesTags: ['AdminSettings'],
    }),
    saveAboutContent: builder.mutation<
      unknown,
      { title: string; description: string }
    >({
      query: body => ({
        url: '/admin/cms/about',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminSettings'],
    }),
    sendPushToUser: builder.mutation<
      unknown,
      { userId: string; title: string; message: string }
    >({
      query: body => ({
        url: '/admin/push/send-user',
        method: 'POST',
        body,
      }),
    }),
    broadcastPush: builder.mutation<
      unknown,
      { title: string; message: string; userType?: string }
    >({
      query: body => ({
        url: '/admin/push/broadcast',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetAdminPostsQuery,
  useDeleteAdminPostMutation,
  useGetReportedPostsQuery,
  useHideReportedPostMutation,
  useGetFaqsQuery,
  useGetFaqByIdQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetSupportSettingsQuery,
  useSaveSupportSettingsMutation,
  useGetApiKeysSettingsQuery,
  useSaveApiKeysSettingsMutation,
  useGetTimezoneSettingsQuery,
  useSaveTimezoneSettingsMutation,
  useGetAboutContentQuery,
  useSaveAboutContentMutation,
  useSendPushToUserMutation,
  useBroadcastPushMutation,
} = adminPortalApi
